import { axiosInstance } from '.'
import { GetWebinarsSearchParams } from '../lib/types'
import { removeEmptyValueFromObject } from '../lib/utils/helpers'
import {
  CREATE_WEBINAR,
  GET_WEBINAR_BY_ID,
  GET_WEBINARS_MANAGE,
  UPDATE_WEBINAR_BY_ID,
  UPLOAD_FILE,
} from './endpoints'

const DEFAULT_LIMIT = 10

const DEFAULT_PAGINATION = {
  total: 0,
  limit: DEFAULT_LIMIT,
  count: 0,
  offset: 0,
}

export type WebinarFormData = {
  title: string
  slug: string
  description: string
  youtube_video_id: string
  thumbnail_url?: string | null
  published_at: string
  status: string
}

export type WebinarListItem = {
  id: number
  thumbnail_url: string
  title: string
  description: string
  youtube_video_id: string
  status: 'DRAFT' | 'PUBLISHED'
  published_at: string
  created_at: string
  updated_at: string
}

export type WebinarsListResponse = {
  webinars: WebinarListItem[]
  pagination: typeof DEFAULT_PAGINATION
}

export type WebinarDetailsResponse = {
  data: WebinarFormData
}

export type ServiceResponse = {
  status?: number
  data?: {
    message?: string
    key?: string
  }
}

export const webinarServices = {
  getWebinars: async (params: GetWebinarsSearchParams) => {
    try {
      const data = await axiosInstance.get(GET_WEBINARS_MANAGE, {
        params: removeEmptyValueFromObject(params),
      })
      return {
        webinars: data.data.webinars,
        pagination: {
          count: data.data.count,
          limit: data.data.limit,
          offset: data.data.offset,
          total: data.data.total,
        },
      } as const
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        return (error as { response: unknown }).response
      }
      return error
    }
  },

  getWebinarById: async (id: string) => {
    try {
      const data = await axiosInstance.get(GET_WEBINAR_BY_ID(id))
      return { data: data.data }
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        return (error as { response: unknown }).response
      }
      return error
    }
  },

  createWebinar: async (payload: WebinarFormData) => {
    try {
      const res = await axiosInstance.post(CREATE_WEBINAR, payload)
      return res
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        return (error as { response: unknown }).response
      }
      return error
    }
  },

  updateWebinar: async (id: string, payload: Partial<WebinarFormData>) => {
    try {
      return await axiosInstance.patch(UPDATE_WEBINAR_BY_ID(id), payload)
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        return (error as { response: unknown }).response
      }
      return error
    }
  },

  uploadFile: async (file: File) => {
    try {
      const formData = new FormData()
      formData.append('file', file)
      return await axiosInstance.post(UPLOAD_FILE, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        return (error as { response: unknown }).response
      }
      return error
    }
  },
}
